// CFG.A4 — Phase Configuration Defaults section.
//
// Tenant admins set organisation-wide defaults per (phase, dimension).
// Projects can override (covered by CFG.A6, not here). Each tab corresponds
// to one phase; Save is per-tab so admins don't accidentally persist
// half-edited forms across phases.

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api';

type Source = 'platform' | 'partner' | 'tenant' | 'project';

interface PhaseDimension {
  key:      string;
  label:    string;
  type:     'select' | 'number' | 'multiselect' | 'toggle' | 'text';
  options?: string[];
  helpText?: string;
  value:    string;
  source:   Source;
  partnerInherited: boolean;
}

interface PhaseConfigResponse {
  partnerName: string | null;
  phases: Record<string, { dimensions: PhaseDimension[] }>;
}

// Display order + labels for the tab bar. Sprint 11 UX.A1 added phase_2_5
// between Architecture and Sprint Planning — 8 phases total.
const PHASE_TABS: Array<{ key: string; label: string }> = [
  { key: 'phase_1_requirements',   label: 'Requirements' },
  { key: 'phase_2_architecture',   label: 'Architecture' },
  { key: 'phase_2_5_ux_design',    label: 'UX Design' },
  { key: 'phase_3_sprint_planning', label: 'Sprint Planning' },
  { key: 'phase_4_development',    label: 'Development' },
  { key: 'phase_5_code_review',    label: 'Code Review' },
  { key: 'phase_6_testing',        label: 'QA/Testing' },
  { key: 'phase_7_deployment',     label: 'Deployment' },
];

interface Props {
  cardStyle: React.CSSProperties;
}

export default function PhaseDefaultsSection({ cardStyle }: Props) {
  const [data, setData] = useState<PhaseConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(PHASE_TABS[0].key);

  // Per-phase pending edits: phase → dimension → new value. Only dirty
  // dimensions are persisted when the admin clicks Save for the tab, so
  // platform/partner defaults aren't copied into tenant_phase_config by
  // accident.
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedTab, setSavedTab] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await apiFetch<PhaseConfigResponse>('/api/settings/phase-config');
      setData(r);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load phase defaults');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const activeDimensions = useMemo(() => {
    return data?.phases[activeTab]?.dimensions ?? [];
  }, [data, activeTab]);

  function setEdit(phase: string, dimension: string, value: string) {
    setEdits((prev) => ({
      ...prev,
      [phase]: { ...(prev[phase] ?? {}), [dimension]: value },
    }));
    setSavedTab(null);
    setSaveError(null);
  }

  function currentValue(d: PhaseDimension): string {
    return edits[activeTab]?.[d.key] ?? d.value;
  }

  async function handleSave() {
    const pending = edits[activeTab] ?? {};
    const keys = Object.keys(pending);
    if (keys.length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      // Sequential so audit rows are in a stable order and so one failure
      // doesn't silently leave half the tab persisted.
      for (const dimension of keys) {
        const value = pending[dimension];
        await apiFetch('/api/settings/phase-config', {
          method: 'PATCH',
          body:   JSON.stringify({ phase: activeTab, dimension, value }),
        });
      }
      setSavedTab(activeTab);
      // Clear edits for this tab and refresh server state so `source` flips
      // to 'tenant' in the hint under the field.
      setEdits((prev) => {
        const next = { ...prev };
        delete next[activeTab];
        return next;
      });
      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const hasPendingOnActiveTab = Object.keys(edits[activeTab] ?? {}).length > 0;
  const activeTabLabel = PHASE_TABS.find((t) => t.key === activeTab)?.label ?? '';

  return (
    <div style={cardStyle} data-testid="panel-phase-defaults">
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        Phase Configuration Defaults
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Set organisation-wide defaults for each phase. Projects can override these settings.
      </p>

      {loading ? (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
      ) : loadError ? (
        <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 12 }}>
          {loadError}
        </div>
      ) : (
        <>
          {/* Tab bar */}
          <div
            role="tablist"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              borderBottom: '1px solid var(--border)',
              marginBottom: 16,
            }}
          >
            {PHASE_TABS.map((t) => {
              const isActive = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(t.key)}
                  data-testid={`phase-tab-${t.key}`}
                  style={{
                    padding: '8px 12px',
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    fontWeight: 600,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent',
                    color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    marginBottom: -1,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Field list */}
          {activeDimensions.length === 0 ? (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
              No tenant-level defaults are defined for this phase yet.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {activeDimensions.map((d) => (
                <DimensionField
                  key={d.key}
                  dim={d}
                  value={currentValue(d)}
                  onChange={(v) => setEdit(activeTab, d.key, v)}
                  partnerName={data?.partnerName ?? null}
                />
              ))}
            </div>
          )}

          {saveError && (
            <div
              data-testid="phase-defaults-save-error"
              style={{
                marginTop: 12,
                padding: '8px 12px',
                background: 'rgba(239,68,68,0.08)',
                color: 'var(--error)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)',
                fontSize: 12,
              }}
            >
              {saveError}
            </div>
          )}
          {savedTab === activeTab && !saveError && (
            <div
              data-testid="phase-defaults-save-success"
              style={{
                marginTop: 12,
                padding: '8px 12px',
                background: 'rgba(16,185,129,0.08)',
                color: '#10b981',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)',
                fontSize: 12,
              }}
            >
              Saved
            </div>
          )}

          {activeDimensions.length > 0 && (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !hasPendingOnActiveTab}
              data-testid={`phase-defaults-save-${activeTab}`}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: saving || !hasPendingOnActiveTab ? 'not-allowed' : 'pointer',
                opacity: saving || !hasPendingOnActiveTab ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : `Save ${activeTabLabel} Defaults`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── DimensionField ──────────────────────────────────────────────────────────

interface DimensionFieldProps {
  dim:         PhaseDimension;
  value:       string;
  onChange:    (v: string) => void;
  partnerName: string | null;
}

function DimensionField({ dim, value, onChange, partnerName }: DimensionFieldProps) {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg)',
    color: 'var(--text)',
  };

  let control: React.ReactNode;

  if (dim.type === 'select' && dim.options) {
    control = (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`phase-defaults-${dim.key}`}
        style={inputStyle}
      >
        {/* Empty value only shown if the current effective value is outside
            the option set — keeps stale values visible instead of silently
            snapping to the first option. */}
        {!dim.options.includes(value) && value !== '' && (
          <option value={value}>{value}</option>
        )}
        {dim.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  } else if (dim.type === 'multiselect' && dim.options) {
    const selected = new Set(value ? value.split(',').map((s) => s.trim()).filter(Boolean) : []);
    control = (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {dim.options.map((opt) => {
          const on = selected.has(opt);
          return (
            <label
              key={opt}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                background: on ? 'rgba(99,102,241,0.08)' : 'var(--bg)',
                color: 'var(--text)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => {
                  const next = new Set(selected);
                  if (on) next.delete(opt);
                  else next.add(opt);
                  onChange(Array.from(next).join(','));
                }}
                data-testid={`phase-defaults-${dim.key}-${opt}`}
                style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
              />
              {opt}
            </label>
          );
        })}
      </div>
    );
  } else if (dim.type === 'number') {
    control = (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`phase-defaults-${dim.key}`}
        style={inputStyle}
      />
    );
  } else if (dim.type === 'toggle') {
    const on = value === 'true';
    control = (
      <button
        type="button"
        onClick={() => onChange(on ? 'false' : 'true')}
        data-testid={`phase-defaults-${dim.key}`}
        style={{
          padding: '4px 12px',
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          fontWeight: 600,
          border: `1.5px solid ${on ? '#10b981' : 'var(--border)'}`,
          borderRadius: 'var(--radius-sm)',
          background: on ? 'rgba(16,185,129,0.08)' : 'var(--surface)',
          color: on ? '#10b981' : 'var(--text-secondary)',
          cursor: 'pointer',
        }}
      >
        {on ? 'Enabled' : 'Disabled'}
      </button>
    );
  } else {
    control = (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`phase-defaults-${dim.key}`}
        style={inputStyle}
      />
    );
  }

  return (
    <div>
      <label
        style={{
          display: 'block',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: 6,
        }}
      >
        {dim.label}
      </label>
      {control}
      {dim.helpText && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {dim.helpText}
        </p>
      )}
      {dim.partnerInherited && (
        <p
          data-testid={`phase-defaults-${dim.key}-partner-hint`}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--text-secondary)',
            marginTop: 4,
            fontStyle: 'italic',
          }}
        >
          Inherited from {partnerName ?? 'Partner'}
        </p>
      )}
    </div>
  );
}
